/**
 * The assistant's one avatar slot — always the cap, always the same 38×38 box.
 * Static mark by default; the live 3D cap (thinking mode) takes the slot while
 * a reply is generating. Because both states occupy the identical box, the
 * swap never shifts layout.
 *
 * CLONED from the Console (tc-console src/shell/AssistantAvatar.tsx). The cap
 * here is the SQUARE mark (/brand/thinking-cap-icon.png) — the wide lockup
 * letterboxed to nothing at this size.
 *
 * Fallbacks, in order: prefers-reduced-motion always gets the static artwork,
 * and while the three.js chunk or the GLB is still loading the static artwork
 * stands in — the animated cap is an enhancement, never a hole in the layout.
 */
import { lazy, Suspense } from 'react'
import type { ReactElement } from 'react'
import { Avatar, useMediaQuery } from '@mui/material'

// Lazy: three.js is the heaviest chunk in the app and the animated cap only
// ever appears while a reply is generating — don't make every reader pay for it.
const ThinkingCap3D = lazy(() => import('./ThinkingCap3D'))

export const ASSISTANT_AVATAR_SIZE = 38

export function StaticCapAvatar(): ReactElement {
  return (
    <Avatar
      src="/brand/thinking-cap-icon.png"
      alt="Thinking Cap"
      sx={{
        width: ASSISTANT_AVATAR_SIZE, height: ASSISTANT_AVATAR_SIZE,
        bgcolor: 'transparent', '& img': { objectFit: 'contain' },
      }}
    />
  )
}

export default function AssistantAvatar({ animated = false }: { animated?: boolean }): ReactElement {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  if (!animated || reducedMotion) return <StaticCapAvatar />
  return (
    <Suspense fallback={<StaticCapAvatar />}>
      <ThinkingCap3D thinking size={ASSISTANT_AVATAR_SIZE} fallback={<StaticCapAvatar />} />
    </Suspense>
  )
}
